import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Paper,
  Grid
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import getHeaders from '../components/ReasuableFuncations/getheaders';
import apiService from '../components/ReasuableFuncations/api';

const ClimateDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState('');
  const [error, setError] = useState('');
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedAggregation, setSelectedAggregation] = useState('Total');
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [aggregationCounts, setAggregationCounts] = useState({
    Total: 0,
    Climate_Resilient_Infrastructure: 0,
    Climate_Change_Guidelines: 0,
    Renewable_Energy: 0,
    Climate_Change_Adaptation: 0,
    Financial_Stability: 0,
  });

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (projects.length > 0) {
      handleAggregation('Total');
    }
  }, [projects]);
  
  useEffect(() => {
    // Scroll to the top of the page when the component mounts
    window.scrollTo(0, 0);
  }, []);

  const fetchProjects = async () => {
    const userData = JSON.parse(localStorage.getItem('user'));
          if (!userData) {
        console.error('User data not found. Redirecting to login.');
        return;
      }
    const { selectedOption, regionalNetworkOption, memberStateOption, countryOption } = userData;
    let url = '/projects';
  
    try {
      let response;  
      switch (selectedOption) {
        case 'Comesa':
          response = await apiService.get(url);
          break;
  
        case 'Regional Network':
          if (regionalNetworkOption) {
            response = await apiService.get(`${url}?regionalNetwork=${regionalNetworkOption}`);
          }
          break;
  
        case 'Member State':
          if (memberStateOption) {
            response = await apiService.get(`${url}?country=${countryOption}`);
          }
          break;
  
        default:
          throw new Error('Invalid selection');
      }
  
      if (response.statusText !== 'OK') {
        throw new Error('Failed to fetch projects');
      }
  
      const data = response.data;
      setProjects(data);
      setFilteredProjects(data);
      updateAggregationCounts(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to fetch projects. Please try again later.');
    }
  };

  const updateAggregationCounts = (data) => {
    const counts = {
      Climate_Resilient_Infrastructure: data.filter(project => project.climate === 'Climate_Resilient_Infrastructure').length,
      Climate_Change_Guidelines: data.filter(project => project.climate === 'Climate_Change_Guidelines').length,
      Renewable_Energy: data.filter(project => project.climate === 'Renewable_Energy').length,
      Climate_Change_Adaptation: data.filter(project => project.climate === 'Climate_Change_Adaptation').length,
      Financial_Stability: data.filter(project => project.climate === 'Financial_Stability').length,
      Total: 0
    };
    counts.Total = counts.Climate_Resilient_Infrastructure + counts.Climate_Change_Guidelines + 
                   counts.Renewable_Energy + counts.Climate_Change_Adaptation+ counts.Financial_Stability;

    setAggregationCounts(counts);
  };

  const handleOpenDialog = (description) => {
    setSelectedDescription(description);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDescription('');
  };

  const handleEdit = (project) => {
    navigate('/edit-project', { state: { project } });
  };

  const handleDeleteConfirmation = (projectId) => {
    setProjectToDelete(projectId);
    setOpenDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (projectToDelete) {
      try {
        const response = await apiService.delete(`/api/projects/${projectToDelete}`);

        if (response.statusText !== 'OK') {
          throw new Error('Failed to delete the project');
        }
        setProjects((prevProjects) => prevProjects.filter((project) => project._id !== projectToDelete));
        updateAggregationCounts(projects.filter((project) => project._id !== projectToDelete));
        setSnackbarMessage('Project deleted successfully!');
        setSnackbarSeverity('success');
        setOpenDeleteDialog(false);
        setProjectToDelete(null);
      } catch (err) {
        console.error('Failed to delete the project:', err);
        setSnackbarMessage('Error deleting project.');
        setSnackbarSeverity('error');
      }
    }
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setProjectToDelete(null);
  };

  const handleAggregation = (climateType) => {
    setSelectedAggregation(climateType);

    let filtered = [];
    if (climateType === 'Total') {
      filtered = projects.filter(project => 
        project.climate === 'Climate_Resilient_Infrastructure' ||
        project.climate === 'Climate_Change_Guidelines' ||
        project.climate === 'Renewable_Energy' ||
        project.climate === 'Climate_Change_Adaptation'||
        project.climate === 'Financial_Stability'
      );
    } else {
      filtered = projects.filter(project => project.climate === climateType);
    }
    setFilteredProjects(filtered);
  };

  const formatDate = (dateString) => {
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', options);
  };

  return (
    <Box sx={{ width: '100%', backgroundColor: 'white', margin: 'auto', p: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom align="center">
        Climate Dashboard
      </Typography>

      {error && (
        <Typography variant="body1" color="error" align="center">
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <Grid container spacing={1} direction={{ xs: 'column', sm: 'row' }} justifyContent="center">
          <Grid item>
            <Button
              variant={selectedAggregation === 'Total' ? "contained" : "outlined"}
              color="primary"
              onClick={() => handleAggregation('Total')}
            >
              Total ({aggregationCounts.Total})
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={selectedAggregation === 'Climate_Resilient_Infrastructure' ? "contained" : "outlined"}
              color="primary"
              onClick={() => handleAggregation('Climate_Resilient_Infrastructure')}
            >
              Climate_Resilient_Infrastructure ({aggregationCounts.Climate_Resilient_Infrastructure})
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={selectedAggregation === 'Climate_Change_Guidelines' ? "contained" : "outlined"}
              color="primary"
              onClick={() => handleAggregation('Climate_Change_Guidelines')}
            >
              Climate_Change_Guidelines ({aggregationCounts.Climate_Change_Guidelines})
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={selectedAggregation === 'Renewable_Energy' ? "contained" : "outlined"}
              color="primary"
              onClick={() => handleAggregation('Renewable_Energy')}
            >
              Renewable_Energy ({aggregationCounts.Renewable_Energy})
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant={selectedAggregation === 'Climate_Change_Adaptation' ? "contained" : "outlined"}
              color="primary"
              onClick={() => handleAggregation('Climate_Change_Adaptation')}
            >
              Climate_Change_Adaptation ({aggregationCounts.Climate_Change_Adaptation})
            </Button>

            <Button
              variant={selectedAggregation === 'Financial_Stability' ? "contained" : "outlined"}
              color="primary"
              onClick={() => handleAggregation('Financial_Stability')}
            >
              Financial_Stability ({aggregationCounts.Financial_Stability})
            </Button>
          </Grid>
        </Grid>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel>
                  Project Name
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel>
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel>
                  End Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel>
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel>
                  Climate Type
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel>
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProjects.map((project) => (
              <TableRow key={project._id}>
                <TableCell onClick={() => handleOpenDialog(project.description)} style={{ cursor: 'pointer' }}>
                  {project.projectName}
                </TableCell>
                <TableCell>{formatDate(project.startDate)}</TableCell>
                <TableCell>{formatDate(project.endDate)}</TableCell>
                <TableCell>{project.status}</TableCell>
                <TableCell>{project.climate}</TableCell>
                {/* <TableCell>{project.description}</TableCell> */}
                <TableCell>
                  {project.description.split(" ").length > 11
                    ? `${project.description.split(" ").slice(0, 11).join(" ")}...`
                    : project.description}
                  {project.description.split(" ").length > 11 && (
                    <Button size="small" onClick={() => handleOpenDialog(project.description)}>
                      More
                    </Button>
                  )}
                </TableCell>


                <TableCell align="right">
                  <Button onClick={() => handleEdit(project)}>Edit</Button>
                  <Button color="error" onClick={() => handleDeleteConfirmation(project._id)}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Description</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{selectedDescription}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">Close</Button>
        </DialogActions>
      </Dialog>




      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>Project Description</DialogTitle>
        <DialogContent>
          <Typography>{selectedDescription}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this project?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage('')}
        message={snackbarMessage}
        severity={snackbarSeverity}
        sx={{
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbarSeverity === 'success' ? 'green' : 'red', // Set the background color based on severity
          }
        }}
      />
    </Box>
  );
};

export default ClimateDashboard;